import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Card, Form, Input, Select, Spin, Upload } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { LOADING, SUCCEEDED } from '@constants/fetchStatuses'
import {
  TAX_CLASSIFICATION_OPTIONS,
  TAX_IDENTIFICATION_METHOD_OPTIONS,
  TAX_IDENTIFICATION_METHOD_SSN,
  TAX_IDENTIFICATION_METHOD_EIN,
  W9_FILE_MAX_SIZE_MB,
  W9_FILE_MAX_SIZE,
} from '@constants/taxInformation'
import { selectCurrentUserCompanyId } from '@reducers/currentUser/currentUserSelectors'
import { uploadW9File } from '@reducers/taxProfiles/taxProfilesSlice'

import { selectW9FileStatus, selectTaxProfilesData } from '@reducers/taxProfiles/taxProfilesSelectors'
import StateAutoComplete from '@components/Shared/StateAutoComplete'
import { createStateValidator } from '@helpers/stateValidation'
import ZipInput, { defaultZipRules } from '@components/Shared/ZipInput'

import './TaxInformation.scss'

const labelCol = { span: 24 }

const TaxInformation = ({ loading, subHeadingText, shellUser = {} }) => {
  const dispatch = useDispatch()
  const [taxIdentificationMethod, setTaxIdentificationMethod] = useState('')
  const [w9File, setW9File] = useState(null)
  const [fileBeingUploaded, setFileBeingUploaded] = useState(null)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [uploadingFileError, setUploadingFileError] = useState(null)
  const companyId = useSelector(selectCurrentUserCompanyId)
  const taxProfiles = useSelector(selectTaxProfilesData)
  const w9FileStatus = useSelector(selectW9FileStatus)

  const handleBeforeUpload = (file) => {
    if (file.size >= W9_FILE_MAX_SIZE) {
      setUploadingFileError(`File must be less than ${W9_FILE_MAX_SIZE_MB}MB`)
      return false
    }
    setUploadingFileError(null)
    setUploadingFile(true)
    setFileBeingUploaded(file)
    if (shellUser?.jwt) {
      dispatch(uploadW9File({ companyId, file, jwt: shellUser.jwt }))
    } else {
      dispatch(uploadW9File({ companyId, file }))
    }
  }

  useEffect(() => {
    if (!uploadingFile || w9FileStatus === LOADING) {
      return
    }

    if (w9FileStatus === SUCCEEDED) {
      setW9File(fileBeingUploaded)
    } else {
      setFileBeingUploaded(null)
    }
    setUploadingFile(false)
  }, [uploadingFile, w9FileStatus])

  if (loading) {
    return (
      <Card className="tax-information">
        <h3>Tax Information</h3>
        <div className="tax-profiles-loading-container">
          <Spin size="large" />
        </div>
      </Card>
    )
  }

  if ((taxProfiles || []).length > 0) {
    return (
      <Card className="tax-information">
        <h3>Tax Information</h3>
        <p>
          You have already provided a tax profile. If you need to update, continue signing up and then visit Settings.
        </p>
      </Card>
    )
  }

  return (
    <Card className="tax-information">
      <h3>Tax Information</h3>
      <p>{subHeadingText || 'Please provide your tax information so we can pay you for completed orders'}</p>
      <Form.Item
        labelCol={{ ...labelCol }}
        label="Upload a copy of your W9 file."
        name="w9_file"
        rules={[
          { required: true },
          () => ({
            validator() {
              if (!w9File) {
                return Promise.reject(new Error())
              }
              return Promise.resolve()
            },
          }),
        ]}
      >
        <Upload
          accept=".pdf, application/pdf"
          fileList={w9File ? [w9File] : []}
          onRemove={() => {
            setW9File(null)
            setUploadingFileError(null)
          }}
          beforeUpload={handleBeforeUpload}
          customRequest={() => null}
        >
          <Button
            className="upload-button"
            icon={uploadingFile ? null : <UploadOutlined />}
            type="default"
            disabled={uploadingFile}
          >
            {uploadingFile ? <Spin /> : 'Upload W9 File'}
          </Button>
          {uploadingFileError && (
            <div role="alert" className="ant-form-item-explain-error">
              {uploadingFileError}
            </div>
          )}
        </Upload>
      </Form.Item>
      <Form.Item
        labelCol={{ ...labelCol }}
        label="Legal Name (The name Reggora will use to file with the IRS)"
        name="legal_name"
        rules={[
          {
            required: true,
            message: 'Please enter your legal name',
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        labelCol={{ ...labelCol }}
        label="Business Name/Disregarded Entity Name (If different from above)"
        name="disregarded_entity_name"
        rules={[
          {
            required: false,
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        labelCol={{ ...labelCol }}
        label="Street Address"
        name="address"
        rules={[
          {
            required: true,
            message: 'Please enter a street address',
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        labelCol={{ ...labelCol }}
        label="City"
        name="city"
        rules={[
          {
            required: true,
            message: 'Please enter a city',
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        labelCol={{ ...labelCol }}
        label="State"
        name="state"
        rules={[
          {
            required: true,
            message: 'Please select a state',
          },
          createStateValidator('Please select a valid state from the list'),
        ]}
      >
        <StateAutoComplete placeholder="State" size="large" />
      </Form.Item>
      <Form.Item
        labelCol={{ ...labelCol }}
        label="Zip Code"
        name="zip_code"
        rules={defaultZipRules}
      >
        <ZipInput />
      </Form.Item>
      <Form.Item
        labelCol={{ ...labelCol }}
        label="Tax Classification"
        name="tax_classification"
        rules={[
          {
            required: true,
            message: 'Please select a tax classification',
          },
        ]}
      >
        <Select default="individual_sole_proprietor_single_member_llc" options={TAX_CLASSIFICATION_OPTIONS} />
      </Form.Item>
      <Form.Item
        labelCol={{ ...labelCol }}
        label="Tax Identification Method (SSN or EIN)"
        name="taxIdentificationMethod"
        rules={[
          {
            required: true,
            message: 'Please select a tax identification method',
          },
        ]}
      >
        <Select
          onChange={(value) => setTaxIdentificationMethod(value)}
          value={taxIdentificationMethod}
          options={TAX_IDENTIFICATION_METHOD_OPTIONS}
        />
      </Form.Item>
      {taxIdentificationMethod === TAX_IDENTIFICATION_METHOD_SSN && (
        <>
          <Form.Item
            labelCol={{ ...labelCol }}
            style={{ width: '60%' }}
            label="Social Security Number (SSN)"
            name="ssn"
            className="tax-identification-number"
            rules={[
              {
                required: taxIdentificationMethod === TAX_IDENTIFICATION_METHOD_SSN,
              },
              {
                pattern: /^\d{9}$/,
                message: 'Please enter a valid 9 digit SSN',
              },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            labelCol={{ ...labelCol }}
            style={{ width: '60%' }}
            label="Confirm SSN"
            name="confirm_ssn"
            className="tax-identification-number"
            dependencies={['ssn']}
            rules={[
              {
                required: taxIdentificationMethod === TAX_IDENTIFICATION_METHOD_SSN,
              },
              {
                pattern: /^\d{9}$/,
                message: 'Please enter a valid 9 digit SSN',
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('ssn') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error("The SSN's you entered do not match"))
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </>
      )}
      {taxIdentificationMethod === TAX_IDENTIFICATION_METHOD_EIN && (
        <>
          <Form.Item
            labelCol={{ ...labelCol }}
            style={{ width: '60%' }}
            label="Employer Identification Number (EIN)"
            name="ein"
            className="tax-identification-number"
            rules={[
              {
                required: taxIdentificationMethod === TAX_IDENTIFICATION_METHOD_EIN,
              },
              {
                pattern: /^\d{9}$/,
                message: 'Please enter a valid 9 digit EIN',
              },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            labelCol={{ ...labelCol }}
            style={{ width: '60%' }}
            label="Confirm EIN"
            name="confirm_ein"
            className="tax-identification-number"
            dependencies={['ein']}
            rules={[
              {
                required: taxIdentificationMethod === TAX_IDENTIFICATION_METHOD_EIN,
              },
              {
                pattern: /^\d{9}$/,
                message: 'Please enter a valid 9 digit EIN',
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('ein') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error("The EIN's you entered do not match"))
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </>
      )}
    </Card>
  )
}

export default TaxInformation
